/**
 * Tooltip — всплывающая подсказка, живущая вне потока страницы.
 *
 * Ключевое: пузырь рендерится порталом в `body`, а не рядом с триггером.
 * Подсказка, лежащая внутри разметки, наследует все ограничения предков —
 * а их на этой странице два, и оба неустранимы:
 *
 *   - `SplitCard` держит `overflow: clip`, потому что общее скругление
 *     группы работает только с обрезкой. Пузырь внутри него обрезается.
 *   - Абсолютный элемент, торчащий за правый край контейнера, увеличивает
 *     его `scrollWidth` даже будучи невидимым. В календаре 128 таких
 *     подсказок давали полосу прокрутки, за которой ничего не скрывалось.
 *
 * Портал снимает оба: пузырь ничей не потомок, обрезать и растягивать
 * его некому. Позиция считается от прямоугольника триггера в момент
 * наведения и зажимается в границы окна.
 *
 * @format
 */

import {useCallback, useRef, useState, type MouseEvent, type ReactNode} from 'react';

import {createPortal} from 'react-dom';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {spacingPx} from 'ui/components/layout/spacing';
import {DS_SCOPE_CLASS} from 'ui/theme/scope';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {RadiusM} from 'ui/tokens/ts/radii';

export interface TooltipProps {
  /** Содержимое подсказки. */
  content: ReactNode;
  /** Максимальная ширина пузыря. */
  width?: number;
  /**
   * Подсказка идёт за курсором, а не висит над триггером.
   *
   * Нужна там, где триггеров много и они узкие: в календаре 128 колонок
   * по три пикселя, и подсказка, привязанная к триггеру, прыгала бы над
   * графиком, не показывая, на каком дне стоит курсор.
   */
  followCursor?: boolean;
  /** Без подложки: только текст. */
  bare?: boolean;
  children: ReactNode;
  className?: string;
}

const Anchor = styled(Box)({
  display: 'inline-flex',
  position: 'relative',
});

const Bubble = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$top: number; $left: number; $width: number; $cursor: boolean; $bare: boolean}>(
  ({theme, $top, $left, $width, $cursor, $bare}) => ({
    ...text('body.m'),
    position: 'fixed',
    top: $top,
    left: $left,
    // У курсора подсказка растёт вправо-вниз от точки; у триггера —
    // центрируется над ним.
    transform: $cursor ? 'none' : 'translate(-50%, -100%)',
    maxWidth: $width,
    width: 'max-content',
    color: surfaceVar('onSurface'),
    pointerEvents: 'none',
    zIndex: theme.zIndex.tooltip,
    textAlign: 'left',
    whiteSpace: 'normal',
    ...($bare
      ? null
      : {
          padding: spacingPx('s'),
          backgroundColor: surfaceVar('surface3'),
          borderRadius: RadiusM,
          boxShadow: theme.shadows[8],
        }),
  }),
);

/** Отступ пузыря от триггера и от краёв окна. */
const OFFSET = 8;

/** Зазор от точки курсора — чтобы подсказка не оказалась под указателем. */
const CURSOR_GAP = 16;

export function Tooltip({
  content,
  width = 280,
  followCursor = false,
  bare = false,
  children,
  className,
}: TooltipProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{top: number; left: number} | null>(null);

  /** Точка у курсора: вправо-вниз, с отскоком от правого и нижнего края. */
  const atCursor = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const left = Math.min(event.clientX + CURSOR_GAP, window.innerWidth - width - OFFSET);
      const top = Math.min(event.clientY + CURSOR_GAP, window.innerHeight - OFFSET * 4);
      setPos({top, left});
    },
    [width],
  );

  /** Точка над триггером: по центру его ширины. */
  const atAnchor = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const half = width / 2;
    const left = Math.min(
      window.innerWidth - half - OFFSET,
      Math.max(half + OFFSET, r.left + r.width / 2),
    );
    setPos({top: r.top - OFFSET, left});
  }, [width]);

  const hide = useCallback(() => setPos(null), []);

  return (
    <>
      <Anchor
        ref={ref}
        className={className}
        onMouseEnter={followCursor ? atCursor : atAnchor}
        onMouseMove={followCursor ? atCursor : undefined}
        onMouseLeave={hide}
        onFocus={atAnchor}
        onBlur={hide}
      >
        {children}
      </Anchor>
      {pos &&
        createPortal(
          <Bubble
            // Пузырь в body, вне поддерева острова — baseline несёт на себе.
            className={DS_SCOPE_CLASS}
            role='tooltip'
            $top={pos.top}
            $left={pos.left}
            $width={width}
            $cursor={followCursor}
            $bare={bare}
          >
            {content}
          </Bubble>,
          document.body,
        )}
    </>
  );
}
